// =====================================================================================
// INVRT Radar - web console - HIGH-INTENT campaign runs (story P2-UI)
//
// Run status and result summaries for ONE campaign (GET /v1/radar/campaigns/{id}/runs),
// reached from that campaign's detail route - the campaign-first IA. Story N3's
// picker-based screen is gone: the campaign is chosen by opening the campaign, not by
// re-picking it inside a tab.
//
// There is deliberately NO trigger control: the canonical route table carries reads only,
// and runs are started by the HIGH-INTENT worker on its schedule. Offering a start button
// would promise an operation the seam cannot perform - a silent failure waiting to happen.
// The empty state says where runs come from instead.
// =====================================================================================

import { isApiError } from "@/lib/api";
import { EmptyState, ErrorState } from "@/components/States";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TableSkeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow } from "@/components/ui/table";
import type { RadarRun } from "@/lib/types.radar";
import { useRadarCampaignRuns } from "./useRadar";

function triggerLabel(trigger: string | null): string {
  if (trigger === null || trigger === "") return "Unknown";
  return trigger.charAt(0).toUpperCase() + trigger.slice(1).replaceAll("_", " ");
}

function statusTone(status: string | null): "success" | "info" | "danger" | "neutral" {
  if (status === "succeeded") return "success";
  if (status === "running") return "info";
  if (status === "failed") return "danger";
  return "neutral";
}

function statusLabel(status: string | null): string {
  if (status === null || status === "") return "Unknown";
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function formatStamp(stamp: string | null): string {
  return stamp ?? "-";
}

function RunDetails({ run }: { run: RadarRun }) {
  return (
    <div className="flex flex-col gap-1 text-sm text-ink-2">
      <span>{run.provider} · {run.model}</span>
      <span className="text-xs">Prompt version: {run.prompt_version}</span>
      {run.degraded ? <span className="font-medium">Degraded analysis</span> : null}
      <span className="inv-mono text-xs">Cost (USD): {run.cost_usd ?? "Not available"}</span>
    </div>
  );
}

export function CampaignRuns({ campaignId }: { campaignId: string }) {
  const runs = useRadarCampaignRuns(campaignId);
  const denied = runs.isError && isApiError(runs.error) && runs.error.httpStatus === 403;

  if (campaignId === "") {
    return (
      <EmptyState
        title="No campaign selected"
        body="Open a campaign from the board to see its analysis runs."
      />
    );
  }

  return (
    <section aria-label="Campaign analysis runs" className="flex flex-col gap-4">
      {runs.isLoading ? <TableSkeleton label="Loading runs…" rows={4} columns={4} /> : null}
      {runs.isError && !denied ? (
        <ErrorState error={runs.error} onRetry={() => void runs.refetch()} />
      ) : null}
      {denied ? (
        <EmptyState
          title="You do not have permission to view these runs."
          body="The server refused this request for the selected organisation."
        />
      ) : null}

      {runs.isSuccess && runs.data.runs.length === 0 ? (
        <EmptyState
          title="No analysis runs yet"
          body="Analysis runs start on the scheduled worker, not from this browser. This list fills when the first run finishes."
        />
      ) : null}

      {runs.isSuccess && runs.data.runs.length > 0 ? (
        <>
          <p className="text-sm text-ink-2">Showing {runs.data.runs.length} of {runs.data.summary.total_runs} recorded analysis runs.</p>
          <div className="hidden overflow-x-auto md:block">
            <Table aria-label="Analysis runs desktop view">
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Created</TableHeaderCell>
                  <TableHeaderCell>Analysis type</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                  <TableHeaderCell>Analysis details</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {runs.data.runs.map((run, index) => (
                  <TableRow key={run.id ?? `run-${index}`}>
                    <TableCell className="font-mono text-xs">{formatStamp(run.created_at)}</TableCell>
                    <TableCell>{triggerLabel(run.trigger)}</TableCell>
                    <TableCell>
                      <Badge tone={statusTone(run.status)}>{statusLabel(run.status)}</Badge>
                    </TableCell>
                    <TableCell>
                      <RunDetails run={run} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <ul aria-label="Analysis runs mobile view" className="grid gap-3 md:hidden">
            {runs.data.runs.map((run, index) => (
              <li key={run.id ?? `run-mobile-${index}`}>
                <Card>
                  <CardHeader className="flex-row items-start justify-between gap-3">
                    <CardTitle>{formatStamp(run.created_at)}</CardTitle>
                    <Badge tone={statusTone(run.status)}>{statusLabel(run.status)}</Badge>
                  </CardHeader>
                  <CardContent className="flex flex-col gap-2">
                    <p className="text-sm text-ink-muted">Analysis type: {triggerLabel(run.trigger)}</p>
                    <RunDetails run={run} />
                  </CardContent>
                </Card>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </section>
  );
}
